import clsx from 'clsx'
import ConfirmCloseModal from 'components/QuestionnaireRequestAutomated/processor/modal/confirmCloseModal'
import {
  attachmentsSection,
  getFormInstance,
  saveFormInstance,
  selectActiveSection,
  selectExpiredFields,
  selectIncompletedFields,
  selectMarkedNaFields,
  selectSections,
  updateActiveSection,
} from 'components/QuestionnaireRequestAutomated/processor/slices/formInstanceSlice'
import { close, setStep, STEPS } from 'components/QuestionnaireRequestAutomated/processor/slices/interstitialSlice'
import { formatRailsTimestamp } from 'lib/utilities'
import React, { Fragment, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

const SectionNavigator = () => {
  const dispatch = useDispatch()
  const sections = useSelector(selectSections)
  const activeSection = useSelector(selectActiveSection)
  const inCompletedFields = useSelector(selectIncompletedFields)

  return (
    <ul className="questionnaire-request-interstitial-field-sidebar-sections dome-d-flex dome-flex-column dome-gap3">
      {sections?.map((section, index) => {
        const selected = index === activeSection
        const numberIncompletedFields = section?.nodes?.filter((node) =>
          inCompletedFields.includes(node?.question_attributes?.field)
        )?.length
        return (
          <li
            key={section.id}
            aria-selected={selected}
            onClick={() => dispatch(updateActiveSection(index))}
            className="questionnaire-request-interstitial-field-sidebar-section-item"
          >
            <span>{section?.section_attributes?.label}</span>
            <span>{numberIncompletedFields} left</span>
          </li>
        )
      })}
    </ul>
  )
}

const StatusIndicators = () => {
  const numberIncompletedFields = useSelector(selectIncompletedFields).length
  const numberMarkedNaFields = useSelector(selectMarkedNaFields).length
  const numberExpiredFields = useSelector(selectExpiredFields).length

  return (
    <div className="dome-d-flex dome-flex-column dome-gap12">
      <div className="dome-d-flex dome-align-center dome-gap12">
        {numberIncompletedFields === 0 ? (
          <Fragment>
            <span>
              <i className="fa-solid fa-party-horn dome-color-dark-green"></i>
            </span>
            <span className="dome-h3 dome-color-dark-green">all sections completed</span>
          </Fragment>
        ) : (
          <span className="dome-h3 dome-color-darkest-grey">work in progress</span>
        )}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} className="dome-d-flex dome-gap6">
        <li className="dome-d-flex dome-align-center dome-gap6">
          <span className="dome-p1 dome-color-dark-grey">{numberIncompletedFields}</span>
          <span className="dome-p2 dome-color-med-grey">missing</span>
        </li>
        <li className="dome-d-flex dome-align-center dome-gap6">
          <span className="dome-p1 dome-color-dark-grey">{numberExpiredFields}</span>
          <span className="dome-p2 dome-color-med-grey">expired</span>
        </li>
        <li className="dome-d-flex dome-align-center dome-gap6">
          <span className="dome-p1 dome-color-dark-grey">{numberMarkedNaFields}</span>
          <span className="dome-p2 dome-color-med-grey">marked n/a</span>
        </li>
      </ul>
    </div>
  )
}

const AttachmentNavigation = () => {
  const dispatch = useDispatch()
  const activeSection = useSelector(selectActiveSection)
  const showAttachmentsTab = () => dispatch(updateActiveSection(attachmentsSection))
  const backgroundColor = activeSection === attachmentsSection ? '#F9F9F9' : ''

  return (
    <div style={{ padding: '3px 12px', borderRadius: 20, backgroundColor }}>
      <div className="dome-d-flex dome-gap6 dome-cursor-pointer" onClick={showAttachmentsTab}>
        <div className="dome-d-flex dome-align-baseline dome-gap3">
          <span>
            <i className="fa-regular fa-paperclip dome-color-dark-green"></i>
          </span>
          <i className="fa-regular fa-x fa-2xs dome-color-dark-green"></i>
        </div>
        <span className="dome-p2 dome-color-darkest-grey dome-text-w500">upload attachments</span>
      </div>
    </div>
  )
}

const Divider = () => <div style={{ height: '1px', background: '#DBDBDB', width: '100%' }}></div>

function Sidebar(props) {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(true)
  const [openCloseModal, setOpenCloseModal] = useState(false)
  const formInstanceId = useSelector((state) => state.global.formInstanceId)
  const lastSaveAt = useSelector((state) => state.formInstance.lastSaveAt)

  const save = (e) => {
    e.preventDefault()
    dispatch(saveFormInstance())
  }

  const submit = () => {
    dispatch(saveFormInstance())
    dispatch(getFormInstance(formInstanceId))
    dispatch(setStep(STEPS.sign))
  }

  const onInterstitialClose = useCallback(() => {
    dispatch(updateActiveSection(0))
    dispatch(getFormInstance(formInstanceId))
    setOpenCloseModal(false)
    dispatch(close())
  }, [dispatch, formInstanceId])

  return (
    <div className="dome-relative">
      <button
        type="button"
        onClick={() => setOpen((pre) => !pre)}
        className="questionnaire-request-interstitial-field-sidebar-collapse-btn"
      >
        <span>
          <i className={clsx('fa-solid  dome-color-med-grey', open ? 'fa-chevron-right ' : 'fa-chevron-left')}></i>
        </span>
      </button>
      <div aria-expanded={open} className="questionnaire-request-interstitial-field-sidebar-container">
        <div className="questionnaire-request-interstitial-field-sidebar-content">
          <button
            type="button"
            style={{ right: 20, top: 12 }}
            onClick={() => setOpenCloseModal(true)}
            className="dome-absolute dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-cancel"
          ></button>
          <div className="dome-d-flex dome-flex-column dome-gap24">
            <StatusIndicators />
            <Divider />
            <SectionNavigator />
            <Divider />
            <AttachmentNavigation />
            <div className="dome-d-flex dome-flex-column dome-gap6">
              <div className="dome-d-flex dome-justify-between">
                <button onClick={save} type="button" className="dome-btn dome-btn-base dome-btn-green-stroke">
                  save
                </button>
                <button type="button" onClick={submit} className="dome-btn dome-btn-base dome-btn-dark-green">
                  generate form
                </button>
              </div>
              <div className="dome-d-flex dome-justify-end">
                <span style={{ width: 150, textAlign: 'end' }} className="dome-p3 dome-color-med-grey">
                  last save: {formatRailsTimestamp(lastSaveAt)?.toLowerCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmCloseModal
        open={openCloseModal}
        onClose={() => setOpenCloseModal(false)}
        onSubmit={onInterstitialClose}
      />
    </div>
  )
}

export default Sidebar
